// 2026-05-26 - FormuLearning - Step 3 카드 컨테이너
// 2026-09-22 수정 - 이슈 #67 S4-2: Lot 조작 뒤 Y 파생 행 재조회(handleLotsSaved, BR-RND-05).
// 2026-09-24 수정 - 이슈 #75(G4): `Pull from Reference Drug` 폐지(`M901-14`) — 대조약 선택 때
//                   Step 1 이 묻고 갈아끼운다(`BR-S3-10`). 그 갱신을 `useStep3Sync` 로 받아 X·총중량을 다시 읽는다.
//                   **주성분 확정**(`M915-04`, BR-STEP-03) — 첫 사용자 저장 직전에 묻는다(`ensureSubstanceFixed`).
// 2026-09-26 수정 - 이슈 #97: 읽기 전용(`BR-STEP-01`, current_step ≥ 4)이면 마운트 계기 자동 쓰기
//                   (총중량 자동 채움 PUT · Y 기본 행 시딩)를 하지 않는다 — 화면을 연 것만으로 학습 전제가 바뀌지 않게.
//                   판정은 정본대로 화면이 한다(서버 가드는 두지 않는다).
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 QTPP·총중량·X·Y 를 잠그고 마운트 자동 쓰기(총중량 자동 채움·Y 시딩)를 하지 않는다.
// 파일 위치: frontend/src/features/step3/Step3Card.tsx

import { useEffect, useState, useCallback } from 'react'
import { useProjectStore } from '../../stores/projectStore'
import { getStep1 } from '../../api/step1'
import { getStep2, updateQtpp } from '../../api/step2'
import type { QTPPRow } from '../../api/step2'
import { getStep3, updateStep3, normalizeRldExperiment } from '../../api/step3'
import type { Step3Data, XVariable, YVariable, RLDExperiment } from '../../api/step3'
import { useSummaryStore } from '../../stores/summaryStore'
import { useStep3Sync } from '../../stores/step3SyncStore'
import { ensureSubstanceFixed } from './substanceFix'
import { pullDefault, positiveNumber, numericEquals } from '../../lib/pullDefault'
import { useDeferredSave } from '../../hooks/useDeferredSave'
import { useCanWrite } from '../../hooks/useCanWrite'
import { isReadOnly } from '../../lib/readOnly'
import QtppSection from '../step2/QtppSection'
import TotalWeightSection from './TotalWeightSection'
import XVariableSection from './XVariableSection'
import YVariableSection from './YVariableSection'
import RldExperimentSection from './RldExperimentSection'

function normalizeStep3(d: Step3Data): Step3Data {
  return {
    ...d,
    x_variables: d.x_variables ?? [],
    y_variables: d.y_variables ?? [],
    // 2026-07-18 - 다중 lot 정규화(이슈 #40): 구 단일-lot shape도 { lots, active_lot }로 승격.
    rld_experiment: normalizeRldExperiment(d.rld_experiment),
    runs: d.runs ?? [],
  }
}

// 이슈 #15 F-4 Part 2 — Step 1에서 선택한 RLD의 weight_mg를 읽어 Step 3 총중량 기본값으로 채운다.
// 2026-07-09 - 이슈 #33 6단계: 전파 규칙 P1(RLD중량→Step3)을 표준 pull 헬퍼(pullDefault)로 정리.
// 수동 편집·무효·동일값이면 pullDefault가 null을 반환하므로 재기록하지 않는다.
async function autoFillFromRld(
  projectId: string,
  current: number | null,
  isManual: boolean,
): Promise<Step3Data | null> {
  if (isManual) return null // 수동 편집값은 출처 조회 없이 보존(REQ-C3)
  const s1 = await getStep1(projectId)
  const idx = s1.selected_rld_id
  const rld = idx != null ? s1.rld_list[idx] : s1.rld_list[0]
  if (!rld) return null
  const raw = (rld as Record<string, unknown>).weight_mg
  // DB total_weight_mg는 Numeric(10,2)이므로 소수 2자리로 맞춰 불필요한 재기록을 막는다.
  const weight = Math.round(parseFloat(String(raw ?? '')) * 100) / 100
  const next = pullDefault({
    current,
    sourceValue: weight,
    isManual,
    isValid: positiveNumber,
    isEqual: numericEquals(0.005),
  })
  if (next == null) return null
  return updateStep3(projectId, { total_weight_mg: next, total_weight_manual: false })
}

export default function Step3Card() {
  const projectId = useProjectStore((s) => s.currentProject?.id)
  // 2026-09-01 - 이슈 #58 S10(#70, M84-48): Step 4로 넘어가면 학습 전제(QTPP·총중량·X·Y)를
  //   잠근다. 판정 근거는 진행도 한 값이다 — MainPage 와 같은 기준을 쓴다.
  const stepLocked = useProjectStore((s) => (s.currentProject?.current_step ?? 1) >= 4)
  // 이슈 #114: viewer 는 진행도와 무관하게 같은 잠금을 쓴다(입력 비활성 + 자동 쓰기 생략).
  const canWrite = useCanWrite()
  const designLocked = stepLocked || !canWrite
  const [data, setData] = useState<Step3Data | null>(null)
  // 회의(260602) 결정 — QTPP를 Step2에서 Step3 맨 앞으로 이동. 백엔드(step2 데이터)는 그대로 두고
  // 화면만 재배치한다(프런트 재배치). qtpp_rows는 step2에서 읽어와 편집·저장한다.
  const [qtppRows, setQtppRows] = useState<QTPPRow[] | null>(null)

  // 2026-07-09 - 이슈 #33 4단계: 필드 편집을 Autosave 모드에 맞춰 저장(ON=즉시, OFF=버퍼).
  // 2026-09-24 - 이슈 #75(BR-STEP-03): 서버 저장 직전에 주성분 확정을 묻는다. 대조약 프로파일 저장
  // (RldExperimentSection)도 같은 확인을 쓴다. QTPP 는 Step 2 데이터라 대상이 아니다.
  const beforeSave = useCallback(
    (explicit: boolean) => (projectId ? ensureSubstanceFixed(projectId, explicit) : Promise.resolve(true)),
    [projectId],
  )
  const beforeLotSave = useCallback(() => beforeSave(false), [beforeSave])
  const { apply } = useDeferredSave<Step3Data>({
    step: 3, projectId, data, setData, save: updateStep3, normalize: normalizeStep3, beforeSave,
  })
  const setSummary = useSummaryStore((s) => s.setSummary)

  // 2026-07-18 - 이슈 #40: 영속 AI Summary(Vertex, Save 시 생성)를 발행. 표시는 CardShell 위젯이 전담.
  useEffect(() => {
    if (!data) return
    setSummary(3, data.ai_summary, data.ai_summary_ko)
  }, [data, setSummary])

  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    ;(async () => {
      const d = normalizeStep3(await getStep3(projectId))
      if (cancelled) return
      // 이슈 #15 F-4 Part 2 — Step 1에서 선택한 RLD 중량(weight_mg)을 Step 3 §3.1
      // total_weight_mg 기본값으로 자동 채운다. RLD 변경 시 카드 재마운트마다 갱신하되,
      // 사용자가 직접 수정한 값(total_weight_manual=true)은 덮어쓰지 않는다(default-fill).
      // 2026-09-26 - 이슈 #97: 읽기 전용(BR-STEP-01)이면 자동 채움을 하지 않는다 — 저장값을 그대로 보인다.
      //   판정은 이 시점의 진행도로 한다(designLocked 와 같은 기준, effect 재실행 없이).
      const lockedNow = (useProjectStore.getState().currentProject?.current_step ?? 1) >= 4 || isReadOnly()
      const filled = lockedNow ? null : await autoFillFromRld(projectId, d.total_weight_mg, d.total_weight_manual)
      if (cancelled) return
      setData(filled ? normalizeStep3(filled) : d)
    })()
    // QTPP(step2)를 별도로 읽어 들인다. QtppSection은 마운트 시 rows로 로컬 상태를 초기화하므로
    // 로드 완료(null→배열) 후에만 렌더한다.
    getStep2(projectId).then((s2) => {
      if (!cancelled) setQtppRows(s2.qtpp_rows ?? [])
    })
    return () => {
      cancelled = true
    }
  }, [projectId])

  const handleSaveQtpp = useCallback(async (rows: QTPPRow[]) => {
    if (!projectId) return
    await updateQtpp(projectId, rows)
    const s2 = await getStep2(projectId)
    setQtppRows(s2.qtpp_rows ?? [])
  }, [projectId])

  const handleWeightChange = useCallback(async (v: number) => {
    // 사용자 직접 입력 → 수동 편집 플래그를 세워 이후 RLD 자동 채움이 덮어쓰지 않도록 잠근다.
    await apply({ total_weight_mg: v, total_weight_manual: true })
  }, [apply])

  const handleXChange = useCallback(async (vars: XVariable[]) => {
    await apply({ x_variables: vars })
  }, [apply])

  const handleYChange = useCallback(async (vars: YVariable[]) => {
    await apply({ y_variables: vars })
  }, [apply])
  // 기본 행 시딩은 화면이 넣는 값이라 주성분 확정 확인을 거치지 않는다(#75 BR-STEP-03).
  const handleYSeed = useCallback(async (vars: YVariable[]) => {
    await apply({ y_variables: vars }, { system: true })
  }, [apply])

  // 2026-07-23 - 이슈 #50(P1): 대조약·Lot 저장은 RldExperimentSection이 전용 Lot API
  // (saveReference·createLot·hideLot·setActiveLot)로 직접 수행한다. 여기서는 로컬 상태만
  // 동기화하고 rld_experiment를 updateStep3(위치기반 reconcile 어댑터)로 보내지 않는다.
  const handleRldExpChange = useCallback((exp: RLDExperiment) => {
    setData((d) => (d ? { ...d, rld_experiment: exp } : d))
  }, [])

  // 2026-09-22 - 이슈 #67 S4-2: 활성 Lot 전환·대조약(격자) 저장·Lot 숨김은 서버에서 Y 파생 행을
  // 다시 만든다(BR-RND-05). Lot API 응답에는 Y 가 없으므로 Step 3 를 다시 읽어 **파생 행만**
  // 갈아 끼운다 — 편집 중인 Y 행(Autosave OFF 의 미저장분 포함)은 건드리지 않는다.
  const handleLotsSaved = useCallback(async () => {
    if (!projectId) return
    try {
      const fresh = await getStep3(projectId)
      const serverDerived = (fresh.y_variables ?? []).filter((v) => v.is_derived)
      setData((d) =>
        d ? { ...d, y_variables: [...d.y_variables.filter((v) => !v.is_derived), ...serverDerived] } : d,
      )
    } catch { /* 재조회 실패는 다음 저장 응답에서 맞춰진다 */ }
  }, [projectId])

  // 2026-09-24 - 이슈 #75(BR-S3-10): Step 1 이 대조약 선택으로 X·총중량을 서버에서 갈아끼우면
  // revision 이 오른다. 이 카드의 로컬 상태는 옛 값이라 **그 세 필드만** 서버 기준으로 바꾼다
  // (Y·대조약 Lot 편집 중인 내용은 건드리지 않는다). 첫 렌더(0)는 위 로드 효과가 맡는다.
  const step3Revision = useStep3Sync((s) => s.revision)
  useEffect(() => {
    if (!projectId || step3Revision === 0) return
    let cancelled = false
    getStep3(projectId).then((fresh) => {
      if (cancelled) return
      setData((d) => (d ? {
        ...d,
        x_variables: fresh.x_variables ?? [],
        total_weight_mg: fresh.total_weight_mg,
        total_weight_manual: fresh.total_weight_manual,
      } : d))
    }).catch(() => { /* 다음 조회·저장 응답에서 맞춰진다 */ })
    return () => { cancelled = true }
  }, [projectId, step3Revision])

  if (!data) return <p className="text-xs text-text-secondary">Loading...</p>

  return (
    <div>
      {/* 2026-09-01 - 이슈 #58 S10(#70, M84-48): 역방향 잠금의 **범위를 이 카드가 쥔다**
          (`CardShell readOnlyScope='self'`). Step 4로 넘어가면 BO 학습의 전제인 QTPP·
          총중량·X·Y 변수를 잠그되, **대조약 Lot 영역은 잠그지 않는다**(사용자 확정
          260901) — 대조약은 학습 전제가 아니라 run마다 고르는 비교 대상이고
          (`BR-RND-04`·`BR-RND-08`), 그 Lot을 추가·입력·숨기는 자리가 여기다(Lot 제외는 #75 폐지).
          여기를 잠그면 Step 4에서 새 대조약을 등록할 경로가 사라진다. */}
      {/* QTPP는 기본 접힘이다(M84-49) — Step 3의 작업 대상은 아래 Target Design
          (변수·초기 설계)이고 QTPP는 Step 2에서 채운 참조 자료다.
          잠금은 **자기 안에서** 건다(`locked`) — 아래 fieldset 안에 두면 개폐 토글까지
          죽어 잠긴 스텝에서 QTPP를 펼쳐 볼 수조차 없다(260901 채증이 잡았다). */}
      {qtppRows !== null && (
        <QtppSection rows={qtppRows} onSave={handleSaveQtpp} locked={designLocked} />
      )}
      <fieldset disabled={designLocked} className="m-0 min-w-0 border-0 p-0">
        <TotalWeightSection value={data.total_weight_mg} manual={data.total_weight_manual} onChange={handleWeightChange} />
        <XVariableSection
          variables={data.x_variables}
          totalWeight={data.total_weight_mg}
          onChange={handleXChange}
        />
        {/* 2026-09-26 - 이슈 #97: 읽기 전용이면 Y 기본 행 시딩(자동 쓰기)도 하지 않는다. */}
        <YVariableSection variables={data.y_variables} onChange={handleYChange} onSeed={handleYSeed} readOnly={designLocked} />
      </fieldset>
      <RldExperimentSection
        projectId={projectId!}
        experiment={data.rld_experiment}
        onChange={handleRldExpChange}
        onLotsSaved={handleLotsSaved}
        beforeSave={beforeLotSave}
      />
    </div>
  )
}
